import { Injectable, signal, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CodesService } from '../../../../core/services/codes/codes.service';
import { ClientsService } from '../../../../core/services/clients/clients.service';
import { ConstitutionCapitalStore } from './constitution-capital.store';
import { ConstitutionShareholdersStore } from './constitution-shareholders.store';
import { ConstitutionAdministrationStore } from './constitution-administration.store';

@Injectable()
export class ConstitutionFormStore {
  private readonly codesService = inject(CodesService);
  private readonly clientsService = inject(ClientsService);
  private readonly fb = inject(FormBuilder);
  
  readonly capitalStore = inject(ConstitutionCapitalStore);
  readonly shareholdersStore = inject(ConstitutionShareholdersStore);
  readonly administrationStore = inject(ConstitutionAdministrationStore);

  readonly form: FormGroup = this.fb.nonNullable.group({
    deedDate: this.fb.control<Date | null>(null, { validators: [Validators.required] }),
    deedConcept: this.fb.nonNullable.control<string>('', { validators: [Validators.required] }),
    notary: this.fb.nonNullable.control<string>(''),
    protocol: this.fb.nonNullable.control<string>(''),
    mercantileRegistryProvince: this.fb.nonNullable.control<string>(''),
    registrationDate: this.fb.control<Date | null>(null),
    tome: this.fb.nonNullable.control<string>(''),
    folio: this.fb.nonNullable.control<string>(''),
    sheet: this.fb.nonNullable.control<string>(''),
    section: this.fb.nonNullable.control<string>(''),
    book: this.fb.nonNullable.control<string>(''),
    inscriptionNumber: this.fb.nonNullable.control<string>(''),
    cnaeCode: this.fb.nonNullable.control<string>(''),
    corporateName: this.fb.nonNullable.control<string>('')
  });

  readonly cnaeCodes = signal<any[]>([]);
  readonly isSaved = signal<boolean>(false);


  async init(clientId: number | null): Promise<void> {
    this.cnaeCodes.set(await this.codesService.getCnaeCodes());
    
    this.capitalStore.init();
    this.shareholdersStore.init();
    this.administrationStore.init();

    if (clientId) {
      await this.loadConstitutionData(clientId);
    }
  }

  async loadConstitutionData(clientId: number): Promise<void> {
    try {
      const constitutionData = await this.clientsService.getClientConstitution(clientId);
      const constitution = Array.isArray(constitutionData) ? constitutionData[0] : constitutionData;
      if (constitution) {
        if (constitution.id) {
          if (!(this.form as FormGroup).contains('id')) {
            (this.form as FormGroup).addControl('id', this.fb.nonNullable.control(constitution.id));
          }
        }
        this.form.patchValue({
          deedDate: constitution.deedDate ? new Date(constitution.deedDate) : null,
          deedConcept: constitution.deedConcept,
          notary: constitution.notary,
          protocol: constitution.protocol,
          mercantileRegistryProvince: constitution.mercantileRegistryProvince,
          registrationDate: constitution.registrationDate ? new Date(constitution.registrationDate) : null,
          tome: constitution.tome,
          folio: constitution.folio,
          sheet: constitution.sheet,
          section: constitution.section,
          book: constitution.book,
          inscriptionNumber: constitution.inscriptionNumber,
          cnaeCode: constitution.cnaeCode,
          corporateName: constitution.corporateName
        });
      }
    } catch (error) {
      console.error('Error loading constitution:', error);
    }

    await this.capitalStore.loadCapitalData(clientId);
    await this.shareholdersStore.loadShareholdersData(clientId);
    await this.administrationStore.loadAdministrationData(clientId);
  }

  async saveAll(clientId: number | null): Promise<boolean> {
    if (!clientId) throw new Error('Falta clientId para guardar la Constitucion.');

    const constitutionPayload: any = { ...this.form.getRawValue(), client: clientId };

    try {
      const savedConstitution = await this.clientsService.saveClientConstitution(constitutionPayload);
      const constitutionId = savedConstitution?.id;
      if (!constitutionId) return false;

      // TODO: Delegating saves to sub-stores would be ideal here in a future refactor

      this.isSaved.set(true);
      setTimeout(() => this.isSaved.set(false), 3000);
      return true;
    } catch (error) {
      console.error('Error saving constitution data:', error);
      return false;
    }
  }
}
